import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { QueryContextFromKeys, QueryParamModel, UserModel } from 'models';
import { userService } from 'services';
import { handleDeleteWorkerUserError } from 'utils/errors';

type UserQueryContexts = QueryContextFromKeys<typeof usersKeys>;

const usersKeys = {
  all: [{ scope: 'users' }] as const,
  lists: () => [{ ...usersKeys.all[0], entity: 'list' }] as const,
  usersList: (params?: QueryParamModel | null) => [{ ...usersKeys.lists()[0], params }] as const,
  // Deliberately not derived from lists(): an export must not land on the list query's cache entry.
  exportList: (params?: QueryParamModel | null) =>
    [{ ...usersKeys.all[0], entity: 'export', params }] as const,
};

const fetchUsersListSearch = async ({ queryKey: [{ params }] }: UserQueryContexts['usersList']) =>
  userService.getUserListSearch(params);

const fetchUsersList = async ({ queryKey: [{ params }] }: UserQueryContexts['usersList']) =>
  userService.getUserList(params);

const fetchUsersExport = async ({ queryKey: [{ params }] }: UserQueryContexts['exportList']) =>
  userService.exportUsers(params);

export const useUserListSearch = (params: QueryParamModel | null = null) =>
  useQuery({ queryKey: usersKeys.usersList(params), queryFn: fetchUsersListSearch });

export const useUserList = (params: QueryParamModel | null = null) =>
  useQuery({ queryKey: usersKeys.usersList(params), queryFn: fetchUsersList });

export const useExportUsers = (params?: QueryParamModel | null) =>
  useQuery({
    queryKey: usersKeys.exportList(params),
    queryFn: fetchUsersExport,
    enabled: false,
    // The download is a side effect, never rendered - keeping the entry cached only pins memory.
    gcTime: 0,
  });

export const useEditUser = () => {
  const queryClient = useQueryClient();

  return useMutation({ mutationFn: ({ data }: { data: Partial<UserModel>[] }) => userService.editUser(data), onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usersKeys.lists() });
    }, });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({ mutationFn: (id: ID) => userService.deleteUser(id), onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usersKeys.lists() });
    },
    onError: handleDeleteWorkerUserError, });
};
